import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { whatsappLink } from "../content/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PROMPTS = [
  "How much would my website cost?",
  "I need an online store — where do I start?",
  "What's included in maintenance?",
  "Help me plan my website",
];

const parseSuggestions = (full) => {
  const m = full.match(/SUGGESTIONS:\s*(\[[\s\S]*?\])\s*$/);
  if (!m) return { text: full.trim(), suggestions: null };
  let suggestions = null;
  try { suggestions = JSON.parse(m[1]); } catch { /* keep defaults */ }
  return { text: full.slice(0, m.index).trim(), suggestions };
};

export const AiChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi — I'm the WebStudios AI assistant. Tell me about your business or your website idea, and I'll help you shape it, estimate a starting price, or answer any questions." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(DEFAULT_PROMPTS);
  const [sid] = useState(() => {
    let id = localStorage.getItem("ws_ai_sid");
    if (!id) { id = crypto.randomUUID(); localStorage.setItem("ws_ai_sid", id); }
    return id;
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "visitor", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        const clean = full.split("SUGGESTIONS:")[0].replace(/LEAD:\s*\{[^\n]*\}/, "");
        setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: clean }]);
      }
      const { suggestions: s } = parseSuggestions(full);
      if (Array.isArray(s) && s.length) setSuggestions(s.slice(0, 3));
    } catch {
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: "Sorry — I'm having trouble connecting right now. You can reach us instantly on WhatsApp: 07440 752224." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed left-4 sm:left-6 bottom-5 sm:bottom-6 z-[90]">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-16 left-0 w-[calc(100vw-32px)] sm:w-[400px] bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-[0_24px_80px_rgba(2,6,23,0.16)] flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-[#F0F4FF] border border-[#0055FF]/15 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[#0055FF]" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900 leading-tight">WebStudios AI</p>
                  <p className="text-[11px] text-emerald-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Online — replies instantly
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-chat-close" aria-label="Close AI chat"
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors duration-200">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={scrollRef} className="h-[320px] sm:h-[360px] overflow-y-auto px-4 py-4 space-y-3" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "visitor" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "visitor" ? "bg-[#0055FF] text-white rounded-br-md" : "bg-slate-100 text-slate-700 rounded-bl-md"
                  }`}>
                    {m.content}
                    {busy && i === messages.length - 1 && m.role === "assistant" && (
                      <span className="inline-block w-1.5 h-4 ml-0.5 bg-cyan-500 animate-pulse align-middle" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-4 pb-2 flex gap-2 flex-wrap">
              {suggestions.map((s, i) => (
                <button key={`${s}-${i}`} onClick={() => send(s)} disabled={busy}
                  data-testid={`ai-suggestion-${i}`}
                  className="text-[11px] text-cyan-700 bg-cyan-50 border border-cyan-200 hover:border-cyan-400 rounded-full px-3 py-1.5 transition-colors duration-200 disabled:opacity-50">
                  {s}
                </button>
              ))}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-100 flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="ai-chat-input"
                placeholder="Ask about your website project…" aria-label="Message the AI assistant"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#0055FF] transition-colors duration-200" />
              <button type="submit" disabled={busy || !input.trim()} data-testid="ai-chat-send" aria-label="Send message"
                className="w-10 h-10 shrink-0 rounded-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 flex items-center justify-center text-white transition-colors duration-200">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="ai-chat-whatsapp-fallback"
              className="pb-3 text-center text-[11px] text-slate-400 hover:text-[#25D366] transition-colors duration-200">
              Prefer a human? Chat on WhatsApp →
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        aria-expanded={open}
        className="flex items-center gap-2.5 bg-white border border-slate-200 soft-shadow rounded-full pl-4 pr-5 py-3 text-slate-900 hover:border-[#0055FF]/40 transition-[border-color] duration-200"
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
      >
        <Sparkles className="w-4 h-4 text-cyan-600" />
        <span className="text-sm font-medium">{open ? "Close" : "Ask our AI"}</span>
      </motion.button>
    </div>
  );
};
